import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
  useReactFlow,
} from "@xyflow/react";
import NewNodeHandle from "../components/NewNodeHandle";
import useEdge from "@/hooks/useEdge";
import {FlowMode, WorkflowNodeTypes} from "@/types/node";
import {truncatePath} from "@/utils/index";
import {useNode} from "@/hooks/useNode";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {useSave} from "@/contexts/SaveContext";

export default function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  takeSnapshot,
}: EdgeProps & {takeSnapshot: () => void}) {
  // see the hook for implementation details
  // onClick adds a node in between the nodes that are connected by this edge
  const addNodeOnEdge = useEdge(id);
  const {addSiblingById} = useNode();
  const {getEdge, getNode} = useReactFlow();
  const {getSelectedEdge} = useSelectedElements();
  const {flowMode} = useSave();
  const selectedEdge = getSelectedEdge();

  const edge = getEdge(id);
  const targetNode = edge?.target ? getNode(edge.target) : null;
  const sourceNode = edge?.source ? getNode(edge.source) : null;

  const isSourceGlobal = sourceNode?.type === WorkflowNodeTypes.GLOBAL;
  const isTargetStart = targetNode?.type === WorkflowNodeTypes.START;

  const isTargetPlaceholder =
    targetNode?.type === WorkflowNodeTypes.PLACEHOLDER;

  const showNewHandle = !isSourceGlobal && !isTargetStart;

  const [edgePath, , edgeCenterY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const path = isTargetPlaceholder ? truncatePath(edgePath) : edgePath;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={{
          strokeWidth: 2,
          stroke: "var(--border)",
          opacity: flowMode === FlowMode.LIVE && isTargetPlaceholder ? 0 : 1,
        }}
      />
      {showNewHandle && (
        <EdgeLabelRenderer>
          <div
            style={{
              transform: `translate(${sourceX}px,${edgeCenterY - 2}px)`,
            }}
            className="nodrag nopan"
          >
            <NewNodeHandle
              id={id}
              selectedEdge={selectedEdge}
              onNewNodeSelect={blueprint => {
                addNodeOnEdge(blueprint);

                if (blueprint.type === WorkflowNodeTypes.BRANCH) {
                  const isTargetBranch =
                    targetNode?.type === WorkflowNodeTypes.BRANCH;
                  const isSourceBranch =
                    sourceNode?.type === WorkflowNodeTypes.BRANCH;

                  if (!isTargetBranch && !isSourceBranch) {
                    addSiblingById(targetNode?.id, blueprint);
                  }
                }

                takeSnapshot();
              }}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
